/**
 * 护理看板数据适配器
 * 将后端 Fission API 数据转换为前端看板组件所需格式
 */

import type { DailyScheduleDay, DailyTask } from "@/types/care-plan";
import type {
  DailyDashboard,
  TodayTasksData,
  TaskGroup,
  DashboardTask,
  DaySliderItem,
} from "@/types/daily-dashboard";

/**
 * 图标映射：根据 product_key 或 action_type 返回图标
 */
const PRODUCT_ICONS: Record<string, string> = {
  // 工具类
  gravel_vacuum: "🧹",
  bucket: "💧",
  heater: "🌡️",
  thermometer: "🌡️",
  digital_thermometer: "🌡️",
  air_pump: "💨",
  test_kit: "🧪",
  salt: "🧂",
  // 药物类
  malachite_green: "💊",
  methylene_blue: "💊",
  ich_cure: "💊",
  antifungal: "💊",
  antibiotics: "💊",
  // 默认
  default: "📋",
};

const ACTION_TYPE_ICONS: Record<string, string> = {
  critical: "⚡",
  observation: "👁️",
  routine: "🔄",
};

/**
 * 获取任务图标
 */
export function getTaskIcon(
  productKey?: string | null,
  actionType?: string,
): string {
  if (productKey && PRODUCT_ICONS[productKey]) {
    return PRODUCT_ICONS[productKey];
  }
  if (actionType && ACTION_TYPE_ICONS[actionType]) {
    return ACTION_TYPE_ICONS[actionType];
  }
  return PRODUCT_ICONS.default;
}

/**
 * 将单个后端任务转换为看板任务
 */
function mapTaskToDashboard(
  task: DailyTask,
  completedTaskIds: Set<string>,
): DashboardTask {
  // 任务完成状态：只使用 completedTaskIds 判断
  // 忽略后端的 status 字段，让用户能看到未完成状态
  const isCompleted = completedTaskIds.has(task.id);

  return {
    id: task.id,
    icon: getTaskIcon(task.product_key, task.action_type),
    action: task.action,
    notes: task.badge || undefined,
    productKey: task.product_key || undefined,
    isCritical: task.action_type === "critical" || task.is_emergency,
    isCompleted,
    completedAt: undefined, // 后端暂无此字段
  };
}

/**
 * 将后端扁平数据转换为看板分组数据
 */
export function transformToDailyDashboard(
  backendDayData: DailyScheduleDay,
  completedTaskIds: Set<string>,
): TodayTasksData {
  const criticalTasks = backendDayData.tasks.filter(
    (t) => t.action_type === "critical" || t.is_emergency,
  );
  const routineTasks = backendDayData.tasks.filter(
    (t) => t.action_type !== "critical" && !t.is_emergency,
  );

  const groups: TaskGroup[] = [];

  if (criticalTasks.length > 0) {
    groups.push({
      title: "关键治疗",
      highlightColor: "orange",
      tasks: criticalTasks.map((t) => mapTaskToDashboard(t, completedTaskIds)),
    });
  }

  if (routineTasks.length > 0) {
    groups.push({
      title: "日常维护",
      highlightColor: "blue",
      tasks: routineTasks.map((t) => mapTaskToDashboard(t, completedTaskIds)),
    });
  }

  // 计算是否全部完成：只使用 completedTaskIds 判断
  const allCompleted =
    backendDayData.tasks.length > 0 &&
    backendDayData.tasks.every((t) => completedTaskIds.has(t.id));

  return {
    headerMessage: `${backendDayData.day_label} - ${backendDayData.title}`,
    dayIndex: backendDayData.day_index,
    title: backendDayData.title,
    titleKey: backendDayData.title_key,
    allCompleted,
    groups,
    tomorrowPreview: undefined, // 由外部设置
  };
}

/**
 * 将 7 日数据转换为滑块导航数据
 */
export function transformToDaySlider(
  schedule: DailyScheduleDay[],
  currentDayIndex: number,
  completedTaskIds: Set<string>,
): DaySliderItem[] {
  return schedule.map((day) => {
    // 计算当天已完成任务数：只使用 completedTaskIds 判断
    const completedCount = day.tasks.filter((t) =>
      completedTaskIds.has(t.id),
    ).length;

    // 判断状态：基于任务完成情况，而非仅仅是天数比较
    let status: DaySliderItem["status"];

    if (day.day_index === currentDayIndex) {
      // 当前选中的天
      status = "active";
    } else if (day.tasks_count > 0 && completedCount === day.tasks_count) {
      // 有任务且全部完成
      status = "completed";
    } else if (day.tasks_count === 0 && day.day_index < currentDayIndex) {
      // 无任务的过去天（观察期）
      status = "completed";
    } else {
      // 其他情况（未完成或未来的天）
      status = "future";
    }

    return {
      dayIndex: day.day_index,
      label: day.title || day.day_label,
      titleKey: day.title_key,
      status,
      tasksCount: day.tasks_count,
      completedCount,
      isEmergency: day.is_emergency,
    };
  });
}

/**
 * 生成明日预告文案
 */
export function generateTomorrowPreview(
  tomorrowDay: DailyScheduleDay | undefined,
): string | undefined {
  if (!tomorrowDay) return undefined;

  const hasCritical = tomorrowDay.tasks.some(
    (t) => t.action_type === "critical" || t.is_emergency,
  );

  if (tomorrowDay.is_observation_period) {
    return "明天是观察期，继续保持即可。";
  }

  if (hasCritical) {
    return "明日有换水/下药任务，请提前准备。";
  }

  return "明日是常规护理日，保持水质稳定。";
}

/**
 * 构建完整的每日看板数据
 */
export function buildDailyDashboard(
  schedule: DailyScheduleDay[],
  currentDayIndex: number,
  selectedDayIndex: number,
  completedTaskIds: Set<string>,
): DailyDashboard {
  const selectedDay = schedule.find((d) => d.day_index === selectedDayIndex);
  const tomorrowDay = schedule.find(
    (d) => d.day_index === selectedDayIndex + 1,
  );

  if (!selectedDay) {
    return {
      viewMode: "daily_dashboard",
      navigation: {
        currentDayIndex: selectedDayIndex,
        days: transformToDaySlider(schedule, currentDayIndex, completedTaskIds),
      },
      todayTasks: {
        headerMessage: "今日无特定安排",
        dayIndex: selectedDayIndex,
        allCompleted: true,
        groups: [],
        tomorrowPreview: "请静养观察。",
      },
    };
  }

  const todayTasks = transformToDailyDashboard(selectedDay, completedTaskIds);
  todayTasks.tomorrowPreview = generateTomorrowPreview(tomorrowDay);

  return {
    viewMode: "daily_dashboard",
    navigation: {
      currentDayIndex: selectedDayIndex,
      days: transformToDaySlider(schedule, currentDayIndex, completedTaskIds),
    },
    todayTasks,
  };
}
